"use client";
import { useFontRender } from "@/components/FontRender";
import { useModal } from "@/components/Modal";
import { checkIsDark, createColor, createColorRGB } from "@/lib/color";
import { cn } from "@/lib/utils";
import {
  IMicrosite,
  IMicrositeComponent,
  IMicrositeStyle,
} from "@/types/microsite";
import assetUrl from "@/vars/assetUrl";
import shortUrl from "@/vars/shortUrl";
import { ShareIcon } from "@heroicons/react/24/solid";
import { usePathname } from "next/navigation";
import React, { Fragment, useEffect, useMemo } from "react";
import { showPreferences } from "vanilla-cookieconsent";
import AdUnit from "../AdUnit";
import components from "./components";
import MicrositeCart from "./MicrositeCart";

import MicrositeExtras from "./MicrositeExtras";
import MicrositeFocus, { useMicrositeFocus } from "./MicrositeFocus";
import { MicrositeFooter } from "./MicrositeFooter";
import MicrositeShareModal from "./MicrositeShareModal";

interface Props {
  data: IMicrosite;
  isBuilder?: boolean;
  hideFooter?: boolean;
  domain?: string;
  noBrand?: boolean;
}

interface IMainScreen extends IMicrositeStyle {
  isBuilder?: boolean;
  children: React.ReactNode;
}

export const MainScreen = ({
  isBuilder,
  backgroundColor,
  textColor,
  font,
  buttonBackgroundColor,
  buttonSize,
  buttonShadowColor,
  buttonBorderColor,
  backgroundStyle,
  backgroundImage,
  backgroundDark,
  ...props
}: IMainScreen) => {
  const bgImage = useMemo(() => {
    return backgroundImage ? `url(${assetUrl}${backgroundImage})` : "none";
  }, [backgroundImage]);

  const styles = useMemo(() => {
    const styles = {
      "--dark-color": "#000",
      "--light-color": "#fff",
    };

    if (backgroundColor) {
      styles["backgroundColor"] = createColor(backgroundColor || "#fff", 1);
    }

    if (textColor) {
      styles["--text-color"] = textColor;
    }

    if (buttonBackgroundColor) {
      styles["--link-color"] = createColorRGB(
        checkIsDark(buttonBackgroundColor || "#fff") ? "#fff" : "#333"
      );
    }

    if (buttonBackgroundColor) {
      styles["--link-background"] = createColorRGB(buttonBackgroundColor);
    }

    if (buttonShadowColor) {
      styles["--link-shadow"] = createColorRGB(buttonShadowColor);
    }

    if (buttonBorderColor) {
      styles["--link-border"] = createColorRGB(buttonBorderColor);
    }

    if (font) {
      styles["--font-family"] = font;
    }

    if (bgImage) {
      styles["--bg-image"] = bgImage;
    }

    return styles;
  }, [
    bgImage,
    backgroundColor,
    textColor,
    buttonBackgroundColor,
    buttonShadowColor,
    buttonBorderColor,
    textColor,
    font,
  ]);

  return (
    <div
      className={cn(
        // Main styles
        "min-h-screen flex relative overflow-x-hidden text-[13px] sm:text-base",
        "[background-attachment:fixed] [color:var(--text-color)] [font-family:var(--font-family)] [background-image:var(--bg-image)]",
        // Builder/Non-builder styles
        isBuilder && "flex flex-col",
        !isBuilder && "overflow-x-hidden flex flex-col",
        // Button size styles
        buttonSize === "small"
          ? "[--button-height:2rem] [--button-margin:0rem] [--icon-width:2rem]"
          : "[--button-height:1.5rem] [--button-margin:0.3rem] [--icon-width:2.5rem] sm:[--button-height:2rem] sm:[--button-margin:0.5rem] sm:[--icon-width:2.8rem]",
        // Background styles
        (!backgroundStyle || backgroundStyle === "base") &&
          "[background-repeat:repeat-y] [background-position:center_top]",
        backgroundStyle === "base-burn" &&
          "[background-repeat:repeat] [background-position:center] [background-size:cover]",
        backgroundStyle === "start" &&
          "[background-position:center_top] [background-repeat:no-repeat]",
        backgroundStyle === "end" &&
          "[background-position:center_bottom] [background-repeat:no-repeat]",
        backgroundStyle === "background-repeat-x-y" &&
          "[background-position:center] [background-repeat:repeat]",
        backgroundStyle === "background-repeat-x-y-burn" &&
          "[background-repeat:repeat] [background-position:center] [background-size:cover]",
        backgroundStyle === "background-cover" &&
          "[background-position:center] [background-size:cover] [background-repeat:no-repeat]",
        backgroundStyle === "background-cover-burn" &&
          "[background-position:center] [background-size:cover] [background-repeat:no-repeat]",
        backgroundStyle === "background-contain" &&
          "[background-position:center] [background-size:contain] [background-repeat:no-repeat]",
        backgroundStyle === "background-contain-burn" &&
          "[background-position:center] [background-size:cover] [background-repeat:no-repeat]",
        backgroundStyle === "background-cover-full-blur" &&
          "[background-position:center] [background-size:cover] [background-repeat:no-repeat]"
      )}
      style={styles as any}
    >
      {/* Burn overlay */}
      <div
        className={cn(
          "hidden fixed top-0 left-0 right-0 bottom-0 pointer-events-none",
          (backgroundStyle === "base-burn" ||
            backgroundStyle === "background-repeat-x-y-burn" ||
            backgroundStyle === "background-cover-burn" ||
            backgroundStyle === "background-contain-burn" ||
            backgroundStyle === "background-cover-full-blur") &&
            "block",
          backgroundStyle === "background-cover-full-blur"
            ? "backdrop-blur-md"
            : "backdrop-blur-sm",
          "after:content-[''] after:block after:mx-auto after:h-full after:w-full after:500m:max-w-[650px]",
          backgroundStyle === "base-burn" &&
            "after:[background-image:var(--bg-image)] after:[background-repeat:repeat-y] after:[background-position:center_top]",
          backgroundStyle === "background-repeat-x-y-burn" &&
            "after:[background-image:var(--bg-image)] after:[background-repeat:repeat] after:[background-position:center]",
          backgroundStyle === "background-cover-burn" &&
            "after:[background-image:var(--bg-image)] after:[background-position:center] after:[background-size:cover] after:[background-repeat:no-repeat]",
          backgroundStyle === "background-contain-burn" &&
            "after:[background-image:var(--bg-image)] after:[background-position:center] after:[background-size:contain] after:[background-repeat:no-repeat]",
          backgroundStyle === "background-cover-full-blur" && "after:hidden"
        )}
      />
      {/* Dark overlay */}
      <div
        className={cn(
          "hidden fixed top-0 left-0 right-0 bottom-0 pointer-events-none",
          backgroundDark === "dark" && "block bg-black/40",
          backgroundDark === "darken" && "block bg-black/60"
        )}
      />
      {props.children}
    </div>
  );
};

const componentKinds = Object.entries(components).reduce((prev, [, item]) => {
  prev[item.kind] = item;
  return prev;
}, {});

type ComponentContainerProp = { children: React.ReactNode } & IMicrositeStyle;

const ComponentContainer = ({
  componentPlacement,
  ...props
}: ComponentContainerProp) => {
  return (
    <div
      className={cn(
        "flex-1 pt-10 px-3 flex flex-col justify-start",
        componentPlacement === "center" && "justify-center"
      )}
    >
      {props.children}
    </div>
  );
};

const MicrositeContainer = ({
  data,
  isBuilder,
  hideFooter,
  domain,
  noBrand,
}: Props) => {
  const shareModal = useModal();
  const micrositeFocus = useMicrositeFocus({ data });
  const pathname = usePathname();
  const domainWithPath = useMemo(() => {
    if (!domain) {
      return "";
    }
    try {
      const u = new URL(`https://${domain}${pathname}`);
      return u.pathname !== "/"
        ? `https://${domain}${u.pathname}`
        : `https://${domain}`;
    } catch (e) {}
    return "";
  }, [domain, pathname]);

  const style = useMemo(() => {
    const haveProfile = data?.components?.find((m) => m.kind === "PROFILE");
    if (
      haveProfile &&
      (haveProfile?.data?.backgroundColor || haveProfile?.data?.imageBackground)
    ) {
      data.style.componentPlacement = "start";
    }
    return data.style;
  }, [data.style, data.components]);

  const { fontStyle, RenderFontStyle } = useFontRender({
    textFont: style?.font || "",
  });

  const { componentsRender, activeId } = useMemo(() => {
    let activeId;

    const renderItem = (item, index) => {
      const Component = componentKinds[item.kind] as IMicrositeComponent<any>;
      if (Component.Render) {
        if (typeof item._hidden !== "undefined" && item._hidden === false) {
          activeId = item.id;
        }

        const isFocus =
          micrositeFocus.focusId === item._id && micrositeFocus.isActive;

        return (
          <Fragment key={item.id || item._id}>
            <Component.Render
              id={item.id || item._id}
              data={{
                ...item.data,
                id: item.id || item._id,
                micrositeId: data.id,
              }}
              microsite={data}
              index={index}
              key={item.id || item._id}
              isBuilder={isBuilder}
              isFocus={isFocus}
            />
            {!isBuilder && (
              <MiddleAds
                middleAt={data?.extras?.middleAdsAt}
                index={index}
                noBrand={noBrand}
              />
            )}
          </Fragment>
        );
      }
      return <Fragment key={item.id} />;
    };

    const componentsRender = data?.components
      ?.filter((item) => item.active === true)
      ?.reduce((result, current, index) => {
        const style = current.data?.style || current.data?.imageStyle;
        const kind = current.kind;
        const lastItem = result[result.length - 1];
        if (
          lastItem &&
          lastItem?.groupStyle === style &&
          (style === "2-column" || style === "col-2" || style === "col-3")
        ) {
          if (kind === "IMAGE" && lastItem.items[0].data?.noPadding) {
            current.data.noPadding = true;
          }
          lastItem.items.push({ index, ...current });
        } else if (
          style === "2-column" ||
          style === "col-2" ||
          style === "col-3"
        ) {
          result.push({
            groupStyle: style,
            kind,
            items: [{ index, ...current }],
          });
        } else {
          result.push({
            items: [{ index, ...current }],
          });
        }
        return result;
      }, [])
      ?.map((item, index) => {
        if (
          item.groupStyle === "2-column" ||
          item.groupStyle === "col-2" ||
          item.groupStyle === "col-3"
        ) {
          return (
            <div
              className={cn(
                "flex flex-wrap pt-2 w-full",
                item.kind === "IMAGE" ? "p-2" : ""
              )}
              key={"group_" + index}
            >
              {item.items.map(renderItem)}
            </div>
          );
        } else {
          return renderItem(item.items[0], item.items[0].index);
        }
      });

    return { componentsRender, activeId };
  }, [
    data.components,
    data?.style,
    data?._hasInteract,
    micrositeFocus.focusId,
    micrositeFocus.isActive,
  ]);

  useEffect(() => {
    if (activeId && isBuilder) {
      if (typeof window !== "undefined") {
        const theId = `mcomp-${activeId}`;
        window.document
          .getElementById(theId)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [activeId, isBuilder]);

  const onShare = () => {
    if (isBuilder) return;
    shareModal.setOpen(true);
  };

  return (
    <MainScreen
      isBuilder={isBuilder}
      {...style}
      font={fontStyle?.fontFamily || ""}
    >
      <style>{`body, html { overscroll-behavior-y: none; }`}</style>
      {RenderFontStyle}
      <div className="top-3 right-3 z-50 fixed">
        {!noBrand && (
          <button
            className="bg-white bg-opacity-80 hover:bg-opacity-100 p-3 rounded-full transition-all"
            onClick={onShare}
          >
            <ShareIcon className="w-5 text-gray-900" />
          </button>
        )}
      </div>
      <div className="relative flex flex-col flex-1 mx-auto w-full 500m:max-w-[500px] [--max-container-width:500px]">
        <ComponentContainer {...style}>
          <div className="z-10 relative flex flex-wrap -m-2">
            {componentsRender}
          </div>
        </ComponentContainer>

        {!isBuilder && <MicrositeExtras style={style} noBrand={noBrand} />}

        {hideFooter !== true ? (
          <MicrositeFooter style={style} isBuilder={isBuilder} data={data} />
        ) : (
          <div className="pb-24" />
        )}

        <MicrositeShareModal
          url={domain ? domainWithPath : shortUrl + "/" + data.slug}
          slug={data.slug}
          domain={domain}
          linkId={data.linkId}
          title={data.title}
          noBrand={noBrand}
          {...shareModal}
        />
      </div>
      <MicrositeFocus {...micrositeFocus} />
      {!isBuilder && (
        <a
          href="#"
          className="bottom-0 left-0 z-[99999] absolute bg-white p-1 font-sans text-black text-xs underline"
          onClick={(e) => {
            e.preventDefault();
            showPreferences();
          }}
        >
          Cookie Preferences
        </a>
      )}
      <MicrositeCart data={data} />
    </MainScreen>
  );
};

const MiddleAds = ({ middleAt: _middleAt, index, noBrand }) => {
  const middleAt = _middleAt === 1 ? 3 : _middleAt;

  if (index === middleAt) {
    return (
      <div className="p-2 w-full">
        <AdUnit
          name={
            noBrand ? "sdotsite_microsite_in_content" : "microsite_in_content"
          }
        />
      </div>
    );
  }
  return <Fragment />;
};

export default MicrositeContainer;
